import { Fragment, useMemo, useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { EmptyState } from "@/components/Layout";

/*
 * LA TABELLA, una volta sola.
 *
 * Le pagine di elenco (Fasi, Cicli, Conti, Clienti…) hanno ognuna la sua
 * `<table>` scritta a mano, con lo stesso `border-b border-line`, la stessa
 * riga vuota, la stessa riga espandibile col chevron — e lo stesso dato che
 * finiva ordinato o no a seconda di chi aveva scritto quella pagina quel
 * giorno. Qui la forma è una: colonne dichiarate, ordinamento sul clic
 * dell'intestazione (solo dove la colonna lo permette), stato vuoto,
 * espansione e clic sulla riga facoltativi.
 *
 * **I filtri restano di chi usa la tabella**: `rows` arriva già filtrata.
 * `DataTable` ordina quello che riceve, non decide che cosa mostrare.
 */

export type Align = "left" | "right" | "center";

export interface DataTableColumn<T> {
  /** Chiave stabile: React key della colonna e nome dell'ordinamento. */
  key: string;
  header: ReactNode;
  align?: Align;
  /** Come si legge il valore dalla riga, per il render della cella. */
  cell: (row: T) => ReactNode;
  /**
   * Valore comparabile per l'ordinamento al clic sull'intestazione.
   * Assente = colonna non ordinabile (l'intestazione non risponde al clic).
   */
  sortBy?: (row: T) => string | number | null;
  /** Classi aggiuntive sulla singola cella (es. `machine-id` per un identificativo). */
  className?: string;
}

const ALIGN_CLASS: Record<Align, string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  empty,
  expandedContent,
  onRowClick,
  initialSort,
}: {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Cosa mostrare al posto della tabella quando `rows` è vuoto. */
  empty: { title: string; hint: string; action?: ReactNode };
  /** Presente = ogni riga ha un chevron che apre questo contenuto sotto, dentro la stessa tabella. */
  expandedContent?: (row: T) => ReactNode;
  /** Presente = la riga è cliccabile (oltre a un eventuale chevron o alle azioni nelle celle). */
  onRowClick?: (row: T) => void;
  initialSort?: { key: string; dir: "asc" | "desc" };
}) {
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(initialSort ?? null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortBy) return rows;
    const by = col.sortBy;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = by(a);
      const vb = by(b);
      if (va === null) return vb === null ? 0 : 1;
      if (vb === null) return -1;
      return va < vb ? -dir : va > vb ? dir : 0;
    });
  }, [rows, sort, columns]);

  if (!rows.length) return <EmptyState title={empty.title} hint={empty.hint} action={empty.action} />;

  const toggleSort = (col: DataTableColumn<T>) => {
    if (!col.sortBy) return;
    setSort((s) => (s?.key === col.key ? { key: col.key, dir: s.dir === "asc" ? "desc" : "asc" } : { key: col.key, dir: "asc" }));
  };

  return (
    <div className="overflow-x-auto px-6 py-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left text-xs text-faint">
            {expandedContent && <th className="w-6 py-2" aria-hidden />}
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                aria-sort={sort?.key === col.key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                className={[
                  "py-2 pr-4 font-medium",
                  ALIGN_CLASS[col.align ?? "left"],
                  col.sortBy ? "cursor-pointer select-none hover:text-ink" : "",
                ].join(" ")}
              >
                {col.sortBy ? (
                  <button type="button" onClick={() => toggleSort(col)} className="inline-flex items-center gap-1">
                    {col.header}
                    {sort?.key === col.key && <span aria-hidden>{sort.dir === "asc" ? "▲" : "▼"}</span>}
                  </button>
                ) : (
                  col.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const key = rowKey(row);
            const isOpen = expanded === key;
            return (
              <Fragment key={key}>
                <tr
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`border-b border-line/60 hover:bg-raised/40 ${onRowClick ? "cursor-pointer" : ""}`}
                >
                  {expandedContent && (
                    <td className="py-2.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpanded(isOpen ? null : key);
                        }}
                        aria-label={isOpen ? "Comprimi" : "Espandi"}
                        aria-expanded={isOpen}
                        className="rounded p-0.5 text-muted hover:bg-raised hover:text-ink"
                      >
                        <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                      </button>
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className={["py-2.5 pr-4", ALIGN_CLASS[col.align ?? "left"], col.className ?? ""].join(" ")}>
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
                {expandedContent && isOpen && (
                  <tr className="border-b border-line/60 bg-raised/20">
                    <td colSpan={columns.length + 1} className="px-6 py-3">
                      {expandedContent(row)}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
